import React, { useState, useEffect } from 'react';
// import { useWindowSize } from 'react-use';
import useSWR from 'swr';
import { Box } from 'rebass';
// import { useCelebrationInRoute, useMobileCheckHook } from '@/hooks/celebration';
import Banner from './Banner';
import { useMobileCheckHook } from 'story-media/hooks';
import { get, getSWR, post } from 'utils/api';
import Wrapper from 'babys-first-year/Wrapper';
import GiftCertificateReview from './GiftCertificateReview';
import PaymentPage from './PaymentPage';
import { useCurrentMember } from 'members/hooks/index';
import { postMemberIdentifiers } from 'members/api';
import { formatMemberBody } from 'members/helper';
import useLocation from 'app/hooks/location';
import { useRef } from 'react';

export function calculatePricing(data, selectedPlan) {
  if (!data) return null;

  const productPricing = data.included.find(
    (item) =>
      item.type === 'pricing-plans' &&
      item.id === selectedPlan?.relationships['product-pricing-plan']?.data?.id
  );
  const subscriptionPricing = data.included.find(
    (item) =>
      item.type === 'pricing-plans' &&
      item.id === selectedPlan?.relationships['subscription-pricing-plan']?.data?.id
  );
  const productDiscount = data.included.find(
    (item) =>
      item.type === 'coupons' &&
      item.id === selectedPlan?.relationships['product-pricing-plan-discount']?.data?.id
  );
  const subscriptionDiscount = data.included.find(
    (item) =>
      item.type === 'coupons' &&
      item.id === selectedPlan?.relationships['subscription-discount']?.data?.id
  );
  const packageId = selectedPlan?.id;

  const productPrice = productPricing ? productPricing.attributes['price-in-dollars'] : 0;
  const subscriptionPrice = subscriptionPricing ? subscriptionPricing.attributes['price-in-dollars'] : 0;

  let discountedProductPrice = productPrice;

  if (productDiscount?.attributes['amount-off']) {
    const productDiscountAmount = productDiscount.attributes['amount-off'] / 100;
    discountedProductPrice = productPrice - productDiscountAmount;
  } else if (productDiscount && productDiscount.attributes['percent-off']) {
    discountedProductPrice = productPrice - (productPrice * (productDiscount.attributes['percent-off'] / 100))
  }

  const subscriptionDiscountAmount = subscriptionDiscount && subscriptionDiscount.attributes['percent-off'] === 100;

  const totalAmount = productPrice + subscriptionPrice;
  const productDiscountAmount = Math.ceil(discountedProductPrice !== productPrice ? productPrice - discountedProductPrice : 0)

  const result = {
    totalAmount: parseFloat(totalAmount).toFixed(2),
    secondPrice: productPrice,
    actualPriceLabel: productPricing?.attributes?.['name'],
    finalPrice: parseFloat(discountedProductPrice).toFixed(2),
    discountLabel: productDiscount?.attributes?.['name'],
    oneYearSubscriptionIncluded: !!subscriptionDiscountAmount,
    subscriptionPrice: parseFloat(subscriptionPrice).toFixed(2),
    subscriptionLabel: subscriptionPricing?.attributes?.['description'],
    productDiscountAmount,
    packageId,
    productPricing: productPricing?.attributes,
  };

  return result;
}


function DashboardPrices({ currentChildJournalId, onStepDown, isPreviewGift = false, firePostMemberEvent, ...props }) {
  const member = useCurrentMember();
  const { pathname } = useLocation();
  const scrollToItemRef = useRef(null)

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [previewGift, setPreviewGift] = useState(isPreviewGift);
  const [email, setEmail] = useState(member?.email);
  const [name_of_giver, setNameOfGiver] = useState("with love from Grandma and Grandpa");
  const [name_of_recipient, setNameOfRecipient] = useState('for you');
  const [gift_certificate_style_id, setGiftCertificateStyleId] = useState(null);

  const [orderSummary, setOrderSummary] = useState(null);
  // const [selectedPlanId, setSelectedPlanId] = useState(null);

  const memberId = member?.id
  const isFromGetStarted = pathname.includes('/get-started');
  const isDisabled = (previewGift && !orderSummary) || (!currentChildJournalId && !previewGift && (!email || !name_of_giver || !name_of_recipient || !gift_certificate_style_id))

  useEffect(() => {
    setEmail(member?.email)
  }, [member?.email])

  const isFromMobile = useMobileCheckHook();
  const [quantity, setRequiresQuantity] = useState(1);

  const [codeDetails, setCodeDetails] = useState(null);
  const { data: mainData } = useSWR(getUrl(), getSWR);

  const data = mainData?.data

  const calculatedPricing = calculatePricing(data);

  async function getUsersCouponDetails() {
    try {
      const { data, status } = await get(`/v3/discounts`);
      if (status === 200 || status === 201) {
        const {
          data: { attributes },
        } = data;
        setCodeDetails({
          title: attributes[`coupon-title`],
          message: attributes[`coupon-details`],
          footer: attributes[`coupon-footer`] ? `*${attributes[`coupon-footer`]}` : '',
          isValid: attributes[`coupon-valid`],
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    getUsersCouponDetails();
  }, []);

  useEffect(() => {
    if (calculatedPricing?.packageId)
      generateInvoice()
  }, [calculatedPricing?.packageId, quantity]);

  function getUrl() {
    let param = `?group=giving`;
    if (currentChildJournalId)
      param = `?group=onboarding`;
    // if (celebration?.theme === VIDEO_GUEST_BOOK)
    //   if (dontShowOneTimeOffer) param = 'experiment=special_offer';
    //   else if (showOneTimeOffer) param = 'experiment=one_time_offer';
    return `/v3/child_journals/product_packages${param}`;

  }

  async function generateInvoice(postalCode) {
    const res = await post(`/v3/product_packages/${calculatedPricing?.packageId}/invoices` +
      (quantity !== undefined ? `?quantity=${quantity}` : '') +
      (postalCode ? `&postal_code=${postalCode}` : '')
      + (name_of_giver ? `&name_of_giver=${name_of_giver}` : '')
      + (name_of_recipient ? `&name_of_recipient=${name_of_recipient}` : '')
      + (gift_certificate_style_id ? `&gift_certificate_style_id=${gift_certificate_style_id}` : '')
    );

    if (res.status === 200) {
      const lineItems = res?.data?.initial?.data?.relationships?.['line-items']?.data?.[0]
      setOrderSummary({ ...res?.data?.initial?.data?.attributes, ...res?.data?.initial?.included.find(({ id, type }) => id === lineItems?.id && type.includes(lineItems?.type))?.attributes, id: res?.data?.initial?.data?.id })
    }
  }

  if (data)
    return (
      <>
        {data ? (
          <Box>
            <Wrapper
              isSubmitting={isSubmitting}
              showFooter={!previewGift}
              onSubmit={async () => {
                if (!isDisabled) {
                  setIsSubmitting(true)

                  if (!previewGift) {

                    if (member?.email !== email) {
                      const body = formatMemberBody(memberId, { email });
                      await postMemberIdentifiers(memberId, body, '/identifiers')
                    }
                    firePostMemberEvent('complete_order')
                    setPreviewGift(true)
                  }
                  window.scrollBy({
                    top: 0,
                    behavior: 'smooth'
                  });
                  setIsSubmitting(false)
                } else {
                  if (scrollToItemRef.current)
                    scrollToItemRef.current.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                      inline: 'center',
                    });
                }
              }}
              // mainImage={isFromMobile ? mobilePurchaseGiftBFY : purchaseGiftBFY}
              heading={isFromGetStarted ? 'You’re So Close to Something Special!' : undefined}
              subHeading={isFromGetStarted ? `Every moment of your baby’s first year deserves to be cherished. Complete your purchase to unlock a beautiful, personalized baby book—filled with photos, stories, and videos that will last a lifetime. Start preserving the magic today.` : undefined}
              percentComplete={0}
              bg="aliceBlue"
              backText=""
              onBack={onStepDown}
              maxWidth={isFromMobile ? 'auto' : 384}
              subText={!previewGift && '* Prices in USD. Shipping and taxes not included. Shipping will be charged when the book is shipped.'}
            >
              {codeDetails && (codeDetails.title || codeDetails.message) && (
                <Banner
                  bg={codeDetails.isValid ? 'successBg' : 'pinks.1'}
                  color={codeDetails.isValid ? 'success' : 'pinks.5'}
                  heading={`${codeDetails.isValid ? 'Save' : ''} ${codeDetails.title}`}
                  text={codeDetails.message}
                  subText={codeDetails.footer}
                  p="16px"
                  size='small'
                />
              )}
              {previewGift ?
                <PaymentPage
                  quantity={quantity}
                  calculatedPricing={calculatedPricing}
                  orderSummary={orderSummary}
                  generateInvoice={generateInvoice}
                  name_of_recipient={name_of_recipient}
                  name_of_giver={name_of_giver}
                  currentChildJournalId={currentChildJournalId}
                  onBack={() => setPreviewGift(false)}
                  gift_certificate_style_id={gift_certificate_style_id}
                  firePostMemberEvent={firePostMemberEvent}
                  {...props}
                />
                : <GiftCertificateReview
                  calculatedPricing={calculatedPricing}
                  quantity={quantity}
                  email={email}
                  setEmail={setEmail}
                  setGiftCertificateStyleId={setGiftCertificateStyleId}
                  currentChildJournalId={currentChildJournalId}
                  name_of_recipient={name_of_recipient}
                  name_of_giver={name_of_giver}
                  gift_certificate_style_id={gift_certificate_style_id}
                  setNameOfRecipient={setNameOfRecipient}
                  setNameOfGiver={setNameOfGiver}
                  firePostMemberEvent={firePostMemberEvent}
                  // onStepDown={onStepDown}
                  generateInvoice={generateInvoice}
                  setRequiresQuantity={setRequiresQuantity}
                  orderSummary={orderSummary}
                  ref={scrollToItemRef}
                />}
            </Wrapper>
          </Box>
        ) : null
        }

      </>
    );


}

export default DashboardPrices;
